import React, { useState, useEffect, useRef } from 'react';
import {
  Menu,
  X,
  ArrowRight,
  Target,
  Shield,
  Scale,
  CheckCircle,
  Users,
  Lightbulb,
  TrendingUp,
  ClipboardCheck,
  Handshake,
  Award,
  ChevronDown,
  Plane,
  Bus,
  Fuel,
  Leaf,
  Send
} from 'lucide-react';
import './index.css';

// Custom hook for scroll-triggered animations
const useInView = (options = {}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        // Once triggered, stop observing
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [options]);

  return { ref, isInView };
};

// Logo Components using official PNG images
const LogoFull: React.FC<{ className?: string; variant?: 'chartreuse' | 'moss-green' }> = ({
  className = '',
  variant = 'chartreuse'
}) => (
  <img
    src={variant === 'moss-green' ? '/logo-full-moss-green.png' : '/logo-full-chartreuse.png'}
    alt="C Air - Inspiring Strategic Solutions"
    className={className}
  />
);

const LogoMark: React.FC<{ className?: string; variant?: 'charcoal' | 'chartreuse' }> = ({
  className = '',
  variant = 'chartreuse'
}) => (
  <img
    src={variant === 'charcoal' ? '/logo-mark-charcoal.png' : '/logo-mark-chartreuse.png'}
    alt="C Air"
    className={className}
  />
);

// Animated Hero Logo Mark - uses official brandmark with animation
const AnimatedLogoMark: React.FC = () => {
  return (
    <div className="relative">
      {/* Shadow/glow effect behind the logo */}
      <div className="absolute inset-0 blur-xl opacity-30">
        <LogoMark className="w-80 h-80 object-contain" variant="chartreuse" />
      </div>
      {/* Main logo with fade-in animation */}
      <LogoMark
        className="w-80 h-80 object-contain animate-fade-in-scale"
        variant="chartreuse"
      />
    </div>
  );
};

// Substack icon (not available in lucide-react)
const SubstackIcon: React.FC<{ className?: string; size?: number }> = ({ className, size = 20 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
  </svg>
);

// Official LinkedIn brandmark (filled, LinkedIn blue)
const LinkedInBrandIcon: React.FC<{ className?: string; size?: number }> = ({ className, size = 24 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="#0A66C2"
    aria-hidden="true"
  >
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
  </svg>
);

// Navigation Component
const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-milk-white/95 backdrop-blur-sm shadow-md' : 'bg-milk-white/90'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <a href="/" className="flex items-center">
            <LogoFull className="h-14 w-auto" variant="moss-green" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('approach')}
              className="text-charcoal hover:text-moss-green transition-colors font-medium"
            >
              Our Approach
            </button>
            <button
              onClick={() => scrollToSection('who-we-serve')}
              className="text-charcoal hover:text-moss-green transition-colors font-medium"
            >
              Who We Serve
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-charcoal hover:text-moss-green transition-colors font-medium"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('values')}
              className="text-charcoal hover:text-moss-green transition-colors font-medium"
            >
              Values
            </button>
            <a
              href="https://substack.com/@jasonhazencairllc"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Read the C Air blog"
              className="inline-flex items-center gap-2 text-charcoal hover:text-moss-green transition-colors font-medium"
            >
              <SubstackIcon size={18} />
              Blog
            </a>
            <button
              onClick={() => scrollToSection('contact')}
              className="btn-primary"
            >
              Get Started
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-charcoal"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pb-4 animate-fade-in">
            <div className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection('approach')}
                className="text-charcoal hover:text-moss-green transition-colors font-medium text-left"
              >
                Our Approach
              </button>
              <button
                onClick={() => scrollToSection('who-we-serve')}
                className="text-charcoal hover:text-moss-green transition-colors font-medium text-left"
              >
                Who We Serve
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-charcoal hover:text-moss-green transition-colors font-medium text-left"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('values')}
                className="text-charcoal hover:text-moss-green transition-colors font-medium text-left"
              >
                Values
              </button>
              <a
                href="https://substack.com/@jasonhazencairllc"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Read the C Air blog"
                className="inline-flex items-center gap-2 text-charcoal hover:text-moss-green transition-colors font-medium"
              >
                <SubstackIcon size={18} />
                Blog
              </a>
              <button
                onClick={() => scrollToSection('contact')}
                className="btn-primary text-center"
              >
                Get Started
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection: React.FC = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen flex items-center bg-moss-green pt-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="text-center lg:text-left">
            <p className="text-chartreuse text-lg font-medium tracking-wide uppercase mb-4 animate-fade-in-up">
              Inspiring Strategic Solutions
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-light-chartreuse leading-tight mb-6 animate-fade-in-up animation-delay-100">
              Strategic operational leadership,{' '}
              <span className="text-chartreuse">without the full-time overhead</span>
            </h1>
            <p className="text-xl sm:text-2xl text-light-chartreuse/90 mb-10 max-w-xl leading-relaxed animate-fade-in-up animation-delay-200">
              We support leadership teams during growth, transition, or uncertainty — bringing steady execution, sound judgment, and accountability when it matters most.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-300">
              <button
                onClick={scrollToContact}
                className="group inline-flex items-center justify-center bg-chartreuse text-moss-green px-8 py-4 rounded-lg text-lg font-semibold hover:bg-light-chartreuse transition-all transform hover:-translate-y-0.5 hover:shadow-lg btn-glow"
              >
                Start a Conversation
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </button>
            </div>
          </div>

          {/* Right Column - Hero Image */}
          <div className="hidden lg:flex justify-center items-center">
            <div className="relative animate-fade-in animation-delay-400 w-full">
              <div className="absolute -inset-4 bg-chartreuse/20 rounded-2xl transform rotate-3"></div>
              <img
                src="/hero-teamwork.jpg"
                alt="Team collaboration"
                className="relative rounded-xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 hidden lg:block">
          <ChevronDown className="text-chartreuse animate-bounce" size={32} />
        </div>
      </div>
    </section>
  );
};

// Problem Section
const ProblemSection: React.FC = () => {
  const { ref, isInView } = useInView();

  return (
    <section id="approach" className="py-20 bg-light-chartreuse/30">
      <div ref={ref} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-charcoal mb-6">
            The Challenge Leaders Face
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Growing organizations often reach a critical inflection point where the demands of leadership exceed the capacity of the existing team.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Problem Card 1 */}
          <div className={`bg-white p-8 rounded-xl shadow-lg border-l-4 border-chartreuse card-hover transition-all duration-700 delay-100 ${
            isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            <div className="flex items-start gap-4">
              <div className="bg-light-chartreuse p-3 rounded-lg">
                <Target className="text-moss-green" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-charcoal mb-3">
                  Performance Under Pressure
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  "The business is performing, but leadership decisions are carrying more risk than they should — and everything still depends on a few people."
                </p>
              </div>
            </div>
          </div>

          {/* Problem Card 2 */}
          <div className={`bg-white p-8 rounded-xl shadow-lg border-l-4 border-chartreuse card-hover transition-all duration-700 delay-200 ${
            isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            <div className="flex items-start gap-4">
              <div className="bg-light-chartreuse p-3 rounded-lg">
                <TrendingUp className="text-moss-green" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-charcoal mb-3">
                  Outpacing Your Infrastructure
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  "We've grown faster than our systems, people, or decision-making structure — and it's starting to create risk."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Who We Serve Section
const WhoWeServeSection: React.FC = () => {
  const { ref, isInView } = useInView();

  const industries = [
    {
      title: 'Travel/Transportation',
      icon: <Bus size={36} />
    },
    {
      title: 'Airlines/Aviation',
      icon: <Plane size={36} />
    },
    {
      title: 'Fuel Supply Chain',
      icon: <Fuel size={36} />
    },
    {
      title: 'Renewables & Sustainability',
      icon: <Leaf size={36} />
    }
  ];

  return (
    <section id="who-we-serve" className="py-20 bg-milk-white">
      <div ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-charcoal mb-6">
            Who We Serve
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {industries.map((industry, index) => (
            <div
              key={index}
              className={`bg-white border border-chartreuse/50 p-8 rounded-xl text-center shadow-md card-hover transition-all duration-500 ${
                isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{
                transitionDelay: isInView ? `${index * 100}ms` : '0ms'
              }}
            >
              <div className="text-moss-green mb-4">{industry.icon}</div>
              <h3 className="text-base font-semibold text-moss-green leading-tight">
                {industry.title}
              </h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Mission Section
const MissionSection: React.FC = () => {
  const { ref, isInView } = useInView();

  return (
    <section id="about" className="py-20 bg-dark-teal">
      <div ref={ref} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center transition-all duration-1000 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-light-chartreuse mb-8">
            Our Mission
          </h2>
          <p className="text-xl text-light-chartreuse/90 leading-relaxed max-w-4xl mx-auto">
            We provide strategic operational leadership that helps owners, boards, and executive teams bring clarity to complex businesses — improving profitability, strengthening execution, reducing risk, enhancing culture and enabling confident decision-making without the burden of full-time overhead.
          </p>
          <p className={`text-lg text-chartreuse mt-6 font-medium transition-all duration-1000 delay-300 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            We exist to step into critical moments, align strategy with operations, and deliver results that endure beyond our&nbsp;engagement.
          </p>
        </div>
      </div>
    </section>
  );
};

// Leadership Section
const LeadershipSection: React.FC = () => {
  const { ref, isInView } = useInView();

  return (
    <section className="py-20 bg-milk-white">
      <div ref={ref} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <div className={`transition-all duration-700 ${
            isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            <div className="relative">
              <div className="absolute -inset-4 bg-chartreuse/30 rounded-2xl transform -rotate-3"></div>
              <img
                src="/jason-hazen.jpg"
                alt="Jason Hazen - Founder, C Air LLC"
                className="relative rounded-xl shadow-lg w-full object-cover"
              />
            </div>
          </div>

          {/* Content */}
          <div className={`transition-all duration-700 delay-200 ${
            isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-moss-green mb-4">
              Jason Hazen
            </h2>
            <p className="text-chartreuse font-semibold text-lg mb-6">
              Founder & Principal
            </p>
            <p className="text-charcoal/80 leading-relaxed mb-6">
              With decades of operational leadership experience across diverse industries, Jason brings the perspective earned through navigating complex business challenges firsthand.
            </p>
            <p className="text-charcoal/80 leading-relaxed mb-6">
              He founded C Air to provide growing organizations access to senior-level strategic guidance — delivering clarity, accountability, and results without the burden of full-time overhead.
            </p>
            <a
              href="https://www.linkedin.com/in/jason-hazen-aa5aa96/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Connect with Jason Hazen on LinkedIn"
              className="inline-flex items-center gap-2 text-charcoal hover:text-moss-green transition-colors font-semibold"
            >
              <LinkedInBrandIcon size={24} />
              <span>Connect on LinkedIn</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Approach Section
const ApproachSection: React.FC = () => {
  const { ref, isInView } = useInView();

  return (
    <section className="py-20 bg-moss-green">
      <div ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-light-chartreuse mb-6">
            What Clients Say After Working With Us
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className={`bg-moss-green/50 border border-chartreuse/30 p-8 rounded-xl card-hover transition-all duration-700 delay-100 ${
            isInView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}>
            <figure className="flex flex-col sm:flex-row gap-4 h-full">
              <CheckCircle className="text-chartreuse flex-shrink-0 mt-1" size={28} />
              <div className="flex flex-col flex-1">
                <blockquote className="text-lg text-light-chartreuse/90 leading-relaxed italic">
                  &ldquo;Jason and the C Air team brought a unique combination of technical, commercial, and operational experience to Project Beacon. Their real-world perspective on SAF commercialization, fuel blending, logistics, and the aviation fuel value chain helped strengthen our planning process, challenge key assumptions, and provide greater confidence in important project decisions. Their insights helped us reduce future execution risk and the potential for costly downstream design changes.&rdquo;
                </blockquote>
                <figcaption className="mt-auto pt-6 text-light-chartreuse">
                  <span className="block font-semibold">Sanjiv Dabee</span>
                  <span className="block text-sm text-light-chartreuse/80">Vice-President of Engineering, Aether Fuels</span>
                </figcaption>
              </div>
            </figure>
          </div>

          <div className={`bg-moss-green/50 border border-chartreuse/30 p-8 rounded-xl card-hover transition-all duration-700 delay-200 ${
            isInView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}>
            <figure className="flex flex-col sm:flex-row gap-4 h-full">
              <Shield className="text-chartreuse flex-shrink-0 mt-1" size={28} />
              <div className="flex flex-col flex-1">
                <blockquote className="text-lg text-light-chartreuse/90 leading-relaxed italic">
                  &ldquo;The pandemic forced us to take a hard look at how the business was operating. We did not need someone to tell us to work harder. We needed help identifying what had changed, where we were exposed, and what had to be built for long term growth. Jason and his team got into the details, helped us bring more discipline to the way we worked, and coordinated the right outside support without pulling our team away from the business.&rdquo;
                </blockquote>
                <figcaption className="mt-auto pt-6 text-light-chartreuse">
                  <span className="block font-semibold">Roy Alonso</span>
                  <span className="block text-sm text-light-chartreuse/80">Chief Executive Officer, Cardinal Transportation</span>
                </figcaption>
              </div>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
};

// Values Section with staggered animations
const ValuesSection: React.FC = () => {
  const { ref, isInView } = useInView();

  const values = [
    {
      icon: <Lightbulb size={32} />,
      title: 'Strategic Clarity',
      description: 'We help leaders define what matters most and align decisions accordingly.'
    },
    {
      icon: <ClipboardCheck size={32} />,
      title: 'Disciplined Execution',
      description: 'We turn strategy into action through structure and accountability.'
    },
    {
      icon: <Award size={32} />,
      title: 'Experienced Judgment',
      description: 'We bring perspective earned through real operational leadership.'
    },
    {
      icon: <Target size={32} />,
      title: 'Accountability Over Activity',
      description: 'We focus on results, not motion.'
    },
    {
      icon: <Handshake size={32} />,
      title: 'Partnership with Integrity',
      description: 'We engage as trusted ethical partners, not vendors.'
    },
    {
      icon: <Scale size={32} />,
      title: 'Leadership Without Excess',
      description: 'We are intentionally structured to deliver senior-level impact efficiently.'
    }
  ];

  return (
    <section id="values" className="py-20 bg-moss-green">
      <div ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-light-chartreuse mb-6">
            Our Values
          </h2>
          <p className="text-xl text-light-chartreuse/80 max-w-3xl mx-auto">
            The principles that guide every engagement
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <div
              key={index}
              className={`bg-moss-green/50 border border-chartreuse p-6 rounded-xl shadow-md value-card-hover transition-all duration-500 ${
                isInView
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{
                transitionDelay: isInView ? `${index * 100}ms` : '0ms'
              }}
            >
              <div className="text-chartreuse mb-4 icon-float">
                {value.icon}
              </div>
              <h3 className="text-xl font-bold text-light-chartreuse mb-3">
                {value.title}
              </h3>
              <p className="text-light-chartreuse/80 leading-relaxed">
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Not A Fit Section
const NotAFitSection: React.FC = () => {
  const { ref, isInView } = useInView();

  return (
    <section className="py-20 bg-charcoal">
      <div ref={ref} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`bg-light-chartreuse p-8 sm:p-12 rounded-2xl transition-all duration-700 ${
          isInView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}>
          <h2 className="text-2xl sm:text-3xl font-bold text-moss-green mb-6">
            We're Not Right for Everyone
          </h2>
          <p className="text-lg text-charcoal/90 leading-relaxed mb-6">
            C Air is not a fit for organizations looking for tactical consultants, quick fixes, or validation of decisions already made.
          </p>
          <p className="text-xl text-moss-green font-medium">
            We work best with leaders who value judgment, discipline, and accountability over speed alone.
          </p>
        </div>
      </div>
    </section>
  );
};

// CTA Section with Contact Form
const CTASection: React.FC = () => {
  const { ref, isInView } = useInView();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://formspree.io/f/mnjbzblg', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="py-20 bg-light-chartreuse">
      <div ref={ref} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-moss-green mb-6">
            Ready to Move Forward?
          </h2>
          <p className="text-lg text-moss-green/80 max-w-2xl mx-auto">
            Tell us about your organization and we'll be in touch to schedule a conversation.
          </p>
        </div>

        <div className="max-w-xl mx-auto">
          <div className={`bg-moss-green p-8 rounded-xl shadow-lg transition-all duration-700 delay-100 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            {formStatus === 'success' ? (
              <div className="text-center py-8">
                <div className="bg-chartreuse/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="text-chartreuse" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-light-chartreuse mb-4">
                  Thank You
                </h3>
                <p className="text-light-chartreuse/80">
                  We've received your message and will be in touch soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-light-chartreuse font-medium mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-moss-green/50 border border-chartreuse/30 text-light-chartreuse placeholder-light-chartreuse/50 focus:outline-none focus:border-chartreuse transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-light-chartreuse font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-moss-green/50 border border-chartreuse/30 text-light-chartreuse placeholder-light-chartreuse/50 focus:outline-none focus:border-chartreuse transition-colors"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-light-chartreuse font-medium mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-4 py-3 rounded-lg bg-moss-green/50 border border-chartreuse/30 text-light-chartreuse placeholder-light-chartreuse/50 focus:outline-none focus:border-chartreuse transition-colors"
                    placeholder="Your organization"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-light-chartreuse font-medium mb-2">
                    How can we help? *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-moss-green/50 border border-chartreuse/30 text-light-chartreuse placeholder-light-chartreuse/50 focus:outline-none focus:border-chartreuse transition-colors resize-none"
                    placeholder="Tell us about your organization and what challenges you're facing..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="group w-full inline-flex items-center justify-center bg-chartreuse text-moss-green px-6 py-4 rounded-lg font-semibold hover:bg-light-chartreuse transition-all btn-glow-light disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                    </>
                  )}
                </button>

                {formStatus === 'error' && (
                  <p className="text-red-300 text-center text-sm">
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}

                <p className="text-light-chartreuse/60 text-center text-sm">
                  No sales pitch. No pressure. Just an honest conversation.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer: React.FC = () => (
  <footer className="bg-charcoal py-12">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row justify-between items-center">
        <div className="mb-6 md:mb-0">
          <LogoFull className="h-12 w-auto" />
        </div>
        <div className="flex flex-col items-center md:items-end gap-4">
          <a
            href="https://substack.com/@jasonhazencairllc"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Read the C Air blog"
            className="inline-flex items-center gap-2 text-light-chartreuse/70 hover:text-chartreuse transition-colors"
          >
            <SubstackIcon size={20} />
            <span className="text-sm font-medium">Blog</span>
          </a>
          <p className="text-light-chartreuse/60 text-sm text-center md:text-right">
            &copy; {new Date().getFullYear()} C Air LLC. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  </footer>
);

// Main App Component
function App() {
  return (
    <div className="min-h-screen">
      <Navigation />
      <HeroSection />
      <ProblemSection />
      <ApproachSection />
      <WhoWeServeSection />
      <MissionSection />
      <ValuesSection />
      <LeadershipSection />
      <NotAFitSection />
      <CTASection />
      <Footer />
    </div>
  );
}

export default App;
